import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, RefreshCw, ScanLine } from 'lucide-react';
import { api } from '@/lib/api';
import { useSessao } from '@/hooks/useSessao';
import { numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Cartao, CorpoCartao, Esqueleto } from '@/componentes/ui/primitivos';
import { DialogoAlocacao, type EtiquetaParaAlocar } from './DialogoAlocacao';

/**
 * Alocação de recebimento — a fila de tudo que falta guardar na empresa.
 *
 * As outras duas portas para o mesmo gesto são contextuais: a gaveta de
 * pendências aparece para quem estava olhando o resumo, e a lista "a guardar"
 * aparece para quem abriu um recebimento. Esta aba é a porta de quem chega
 * para FAZER ISTO — o conferente com o coletor na mão, que não quer saber de
 * qual recebimento veio cada volume, quer a fila.
 *
 * O CAMPO DE BIPAR VEM PRIMEIRO
 * -----------------------------
 * Quem trabalha aqui tem um leitor. Bipar a etiqueta e cair direto no diálogo
 * de posição é o caminho curto; a lista abaixo existe para quando o código não
 * lê, ou para escolher pelo que está mais parado.
 */

interface LinhaAGuardar {
  numero: number;
  data: string | null;
  descricao: string | null;
  produto: number | null;
  codigoBarras: string | null;
  sequencia: number | null;
  quantidade: number | null;
}

function dataCurta(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function parado(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dias = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  return dias <= 0 ? 'hoje' : dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
}

export function PainelAlocacao({
  empresa,
  centro,
}: {
  empresa: number | null;
  centro: number | null;
}): JSX.Element {
  const { temPermissao } = useSessao();
  const podeAlocar = temPermissao('recebimento.alocar');
  const [alocando, definirAlocando] = React.useState<EtiquetaParaAlocar | null>(null);
  const [bipado, definirBipado] = React.useState('');

  const consulta = useQuery<{ linhas: LinhaAGuardar[] }>({
    queryKey: ['recebimento-pendencias', 'detalhe', empresa, 'semAlocacao'],
    queryFn: () =>
      api.get<{ linhas: LinhaAGuardar[] }>('/api/recebimentos/pendencias/semAlocacao', {
        empresa,
        dias: 60,
      }),
    enabled: empresa !== null,
    staleTime: 30_000,
  });

  const linhas = consulta.data?.linhas ?? [];

  const abrir = (l: LinhaAGuardar): void =>
    definirAlocando({
      codigoBarras: l.codigoBarras ?? '',
      recebimento: l.numero,
      produto: l.produto ?? 0,
      descricao: l.descricao,
      quantidade: l.quantidade,
      sequencia: l.sequencia,
    });

  /*
   * Bipar procura na lista carregada. Se não achar, manda assim mesmo: o
   * servidor tem a palavra final e a mensagem dele é mais útil que um "não
   * encontrado" nosso — pode ser etiqueta de outra empresa, já alocada, ou de
   * recebimento não aprovado, e cada caso tem um texto próprio.
   */
  const bipar = (): void => {
    const codigo = bipado.trim();
    if (codigo === '') return;
    const achada = linhas.find((l) => (l.codigoBarras ?? '') === codigo);
    definirAlocando(
      achada
        ? {
            codigoBarras: codigo,
            recebimento: achada.numero,
            produto: achada.produto ?? 0,
            descricao: achada.descricao,
            quantidade: achada.quantidade,
            sequencia: achada.sequencia,
          }
        : {
            codigoBarras: codigo,
            recebimento: 0,
            produto: 0,
            descricao: null,
            quantidade: null,
            sequencia: null,
          },
    );
    definirBipado('');
  };

  if (empresa === null) {
    return (
      <Cartao className="grid min-h-[240px] place-items-center px-6 text-center">
        <p className="text-[13px] text-textoFraco">Escolha uma empresa no seletor do topo.</p>
      </Cartao>
    );
  }

  return (
    <div className="space-y-3">
      {!podeAlocar ? (
        <div className="rounded-xl border border-atencao/40 bg-atencao/10 px-3 py-2.5 text-[13px]">
          Você pode ver a fila, mas não alocar. A permissão{' '}
          <strong>Alocar volume recebido em posição</strong> é concedida em Administração &gt;
          Usuários.
        </div>
      ) : null}

      <Cartao>
        <div className="flex flex-wrap items-center gap-2 px-4 py-3">
          <ScanLine className="h-5 w-5 shrink-0 text-textoFraco" />
          <form
            className="flex flex-1 items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              bipar();
            }}
          >
            <input
              value={bipado}
              onChange={(e) => definirBipado(e.target.value.replace(/\D/g, ''))}
              disabled={!podeAlocar}
              autoFocus
              autoComplete="off"
              inputMode="numeric"
              placeholder="bipe a etiqueta de alocação"
              aria-label="Código da etiqueta de alocação"
              className="tabular min-h-[40px] w-full max-w-[320px] rounded-lg border border-bordaForte bg-superficie px-3 font-mono text-[16px] font-bold outline-none focus:border-primaria focus:ring-2 focus:ring-primaria/25"
            />
            <Botao variante="primaria" type="submit" disabled={!podeAlocar || bipado.trim() === ''}>
              <MapPin className="h-4 w-4" />
              Alocar
            </Botao>
          </form>
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            className="ml-auto"
            onClick={() => void consulta.refetch()}
            carregando={consulta.isFetching}
          >
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </Botao>
        </div>
      </Cartao>

      <Cartao className="relative flex min-h-0 flex-col">
        <div className="flex flex-wrap items-baseline gap-2 border-b border-borda px-4 py-2.5">
          <span className="text-[13px] font-bold">A guardar</span>
          <span className="tabular text-[11.5px] text-textoFraco">
            {consulta.isLoading ? '' : `${numero(linhas.length)} volumes · empresa ${empresa}`}
          </span>
        </div>

        <CorpoCartao className="min-h-0 flex-1 overflow-y-auto">
          {consulta.isLoading ? (
            <Esqueleto className="h-64" />
          ) : consulta.isError ? (
            <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
          ) : linhas.length === 0 ? (
            <EstadoVazio
              icone={<MapPin className="h-7 w-7" />}
              titulo="Nada a guardar"
              detalhe="Todo volume etiquetado desta empresa já foi alocado numa posição."
            />
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-borda text-textoFraco">
                  <th className="rotulo pb-1.5 pr-2">Recebido</th>
                  <th className="rotulo pb-1.5 pr-2">Recebimento</th>
                  <th className="rotulo pb-1.5 pr-2">Produto</th>
                  <th className="rotulo pb-1.5 pr-2">Descrição</th>
                  <th className="rotulo pb-1.5 pr-2">Etiqueta</th>
                  <th className="pb-1.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-borda">
                {linhas.map((l, i) => (
                  <tr key={`${l.codigoBarras ?? i}`}>
                    <td className="whitespace-nowrap py-1.5 pr-2 text-[11.5px]">
                      <span className="tabular font-semibold">{dataCurta(l.data)}</span>
                      <span className="ml-1.5 text-textoFraco">{parado(l.data)}</span>
                    </td>
                    <td className="tabular py-1.5 pr-2 font-mono text-[12px] font-bold">
                      {l.numero}
                    </td>
                    <td className="tabular py-1.5 pr-2 font-mono text-[12px]">
                      {l.produto ?? '—'}
                    </td>
                    <td
                      className="max-w-[300px] truncate py-1.5 pr-2 text-[12px]"
                      title={l.descricao ?? ''}
                    >
                      {l.descricao ?? '—'}
                    </td>
                    <td className="whitespace-nowrap py-1.5 pr-2 text-[11.5px]">
                      <span className="tabular font-mono">{l.codigoBarras ?? '—'}</span>
                      <span className="ml-1.5 text-textoFraco">
                        seq {l.sequencia ?? '—'} · {numero(l.quantidade ?? 0)} un
                      </span>
                    </td>
                    <td className="py-1.5 text-right">
                      <Botao
                        variante="secundaria"
                        tamanho="pequeno"
                        disabled={!podeAlocar}
                        onClick={() => abrir(l)}
                      >
                        <MapPin className="h-3.5 w-3.5" />
                        Alocar
                      </Botao>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CorpoCartao>
      </Cartao>

      <DialogoAlocacao
        etiqueta={alocando}
        empresa={empresa}
        centro={centro}
        aoFechar={() => definirAlocando(null)}
        aoConcluir={() => void consulta.refetch()}
      />
    </div>
  );
}
